import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Minimal shape required by the validator. Each requestor must expose a list of
 * their practice area codes plus a display name for error messages.
 */
export interface RequestorCoverageInput {
  id: string;
  name: string;
  practiceAreaCodes: string[];
}

/**
 * Cross-field validator: every selected requestor must have at least one of
 * their own practice areas present in the form control's selected codes.
 *
 * Returns a `requestorCoverage` error containing the names of any requestors
 * whose practice areas are not represented in the current selection.
 *
 * Requestors with no practice areas on record are ignored (we cannot enforce
 * coverage we don't know about).
 */
export function requestorPracticeAreaCoverageValidator(
  getRequestors: () => RequestorCoverageInput[],
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const selected = new Set<string>(
      (control.value as string[] | null | undefined) ?? [],
    );
    const requestors = getRequestors();
    if (requestors.length === 0) {
      return null;
    }

    const uncovered: string[] = [];
    for (const r of requestors) {
      const codes = r.practiceAreaCodes ?? [];
      if (codes.length === 0) {
        continue;
      }
      const covered = codes.some((code) => selected.has(code));
      if (!covered) {
        uncovered.push(r.name);
      }
    }

    return uncovered.length > 0 ? { requestorCoverage: { uncovered } } : null;
  };
}
